<script setup lang="ts">
import {Label} from "@/core/components/ui/label";
import {Input} from "@/core/components/ui/input";
import DrugInteractionAllergies from "@/drug-interaction/components/DrugInteractionAllergies.vue";
import DrugInteractionPathologies from "@/drug-interaction/components/DrugInteractionPathologies.vue";
import {BreastFeedingStatus, HepaticStatus} from "@/customer/interfaces/DeclaredInformation";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import {VidalAllergy, VidalPathology} from "@/customer/interfaces/Patient";

const data = defineModel<{
    weight?: number,
    height?: number,
    amenorrhoea?: number,
    breastFeeding?: BreastFeedingStatus,
    breastFeedingDate?: string,
    hepatic?: HepaticStatus,
    dfg?: number,
    pathologies: VidalPathology[],
    allergies: VidalAllergy[]
}>('data', {required: true});
</script>

<template>
  <div class="space-y-4">
    <div class="input-row">
      <div class="grid w-full items-center gap-1.5">
        <Label for="weight">Poids (kg)</Label>
        <Input id="weight" v-model:model-value="data.weight" type="number" class="form-input" />
      </div>
      <div class="grid w-full items-center gap-1.5">
        <Label for="heigh">Taille (cm)</Label>
        <Input id="heigh" v-model:model-value="data.height" type="number" class="form-input" />
      </div>
    </div>
    <div class="input-row">
      <div class="grid w-full items-center gap-1.5">
        <Label for="amenorrhea">Semaines d'aménorrhée</Label>
        <Input id="amenorrhea" v-model:model-value="data.amenorrhoea" type="number" class="form-input" />
      </div>
      <div class="grid w-full items-center gap-1.5">
        <Label for="breastFeeding">Allaitement</Label>
        <BaseSelect
          v-model:model-value="data.breastFeeding"
          trigger-class="form-input"
          :options="[
            {label: 'Aucune', value: BreastFeedingStatus.NONE},
            {label: 'Moins d\'1 mois', value: BreastFeedingStatus.LESS_THAN_ONE_MONTH},
            {label: 'Plus d\'1 mois', value: BreastFeedingStatus.MORE_THAN_ONE_MONTH},
            {label: 'Allaitement', value: BreastFeedingStatus.ALL}
          ]"
        />
      </div>
    </div>
    <div class="input-row">
      <div class="grid w-full items-center gap-1.5">
        <Label for="breastFeedingDate">Date de début de l'allaitement</Label>
        <Input id="breastFeedingDate" v-model:model-value="data.breastFeedingDate" type="date" class="form-input" />
      </div>
    </div>
    <div class="input-row">
      <div class="grid w-full items-center gap-1.5">
        <Label for="amenorrhea">Insuffisance hépatique</Label>
        <BaseSelect
          v-model:model-value="data.hepatic"
          trigger-class="form-input"
          :options="[
            {label: 'Aucune', value: HepaticStatus.NONE},
            {label: 'Modérée', value: HepaticStatus.MODERATE},
            {label: 'Sévère', value: HepaticStatus.SEVERE}
          ]"
        />
      </div>
      <div class="grid w-full items-center gap-1.5">
        <Label for="dfg">DFG ml/min</Label>
        <Input id="dfg" v-model:model-value="data.dfg" type="number" class="form-input" />
      </div>
    </div>
    <div class="grid w-full items-center gap-1.5">
      <Label for="allergies">Allergies</Label>
      <DrugInteractionAllergies v-model:allergies="data.allergies" />
    </div>
    <div class="grid w-full items-center gap-1.5">
      <Label for="pathologies">Pathologies</Label>
      <DrugInteractionPathologies v-model:pathologies="data.pathologies" />
    </div>
  </div>
</template>

<style scoped>
.input-row {
    @apply flex items-center gap-4;
}

:deep(.form-input) {
    @apply focus:ring-primary !bg-white;
}
</style>
